'use client'

import { useState } from 'react'
import { Save, CheckCircle, AlertCircle, Loader2, Power, PowerOff, Phone, PhoneOff, Pencil } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { HelpDialog } from './HelpDialog'

interface FlowToolbarProps {
  flowName: string
  flowDescription?: string
  isActive: boolean
  isSaving: boolean
  isTogglingActive: boolean
  isSynced: boolean
  syncError?: string | null
  assignedPhones: string[]
  onSave: () => void
  onToggleActive: () => void
  onUpdateDetails: (name: string, description: string) => void
}

export function FlowToolbar({
  flowName,
  flowDescription,
  isActive,
  isSaving,
  isTogglingActive,
  isSynced,
  syncError,
  assignedPhones,
  onSave,
  onToggleActive,
  onUpdateDetails,
}: FlowToolbarProps) {
  const hasAssignedPhones = assignedPhones.length > 0
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [editName, setEditName] = useState(flowName)
  const [editDescription, setEditDescription] = useState(flowDescription || '')

  const handleSaveDetails = () => {
    if (editName.trim()) {
      onUpdateDetails(editName.trim(), editDescription.trim())
      setEditDialogOpen(false)
    }
  }

  return (
    <div className="flex shrink-0 flex-col gap-3 border-b bg-background px-4 py-3 lg:flex-row lg:flex-wrap lg:items-center lg:justify-between lg:py-2">
      <div className="flex min-w-0 flex-wrap items-center gap-2 lg:gap-3">
        <div className="flex min-w-0 flex-1 items-center gap-1 lg:flex-initial">
          <h2 className="min-w-0 break-words font-semibold">{flowName}</h2>
          <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="ghost" size="sm" className="h-9 w-9 shrink-0 p-0 sm:h-6 sm:w-6" aria-label="Edit flow details">
                <Pencil className="w-3 h-3 text-muted-foreground" />
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Edit Flow Details</DialogTitle>
                <DialogDescription>
                  Update the name and description of this flow.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="flow-name">Name</Label>
                  <Input
                    id="flow-name"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    placeholder="Flow name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="flow-description">Description</Label>
                  <Textarea
                    id="flow-description"
                    value={editDescription}
                    onChange={(e) => setEditDescription(e.target.value)}
                    placeholder="Optional description..."
                    rows={3}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSaveDetails} disabled={!editName.trim()}>
                  Save
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
        {isActive ? (
          <Badge variant="default" className="bg-green-500">Active</Badge>
        ) : (
          <Badge variant="secondary">Inactive</Badge>
        )}
      </div>

      <TooltipProvider>
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
          {/* Phone Assignment Status */}
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className="inline-flex shrink-0 rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label={hasAssignedPhones ? `Assigned phone numbers: ${assignedPhones.join(', ')}` : 'No phone numbers assigned'}
              >
              {hasAssignedPhones ? (
                <Badge variant="outline" className="gap-1 whitespace-nowrap text-blue-600 border-blue-500">
                  <Phone className="w-3 h-3" />
                  {assignedPhones.length} <span>phone</span><span>{assignedPhones.length > 1 ? 's' : ''}</span>
                </Badge>
              ) : (
                <Badge variant="outline" className="gap-1 whitespace-nowrap text-orange-600 border-orange-400">
                  <PhoneOff className="w-3 h-3" />
                  <span>No phones</span>
                </Badge>
              )}
              </button>
            </TooltipTrigger>
            <TooltipContent>
              {hasAssignedPhones ? (
                <div className="text-xs">
                  <p className="font-medium mb-1">Assigned phone numbers:</p>
                  {assignedPhones.map(phone => (
                    <p key={phone} className="text-muted-foreground">{phone}</p>
                  ))}
                </div>
              ) : (
                <p className="text-xs">No phone numbers assigned. Go to Phone Config tab to assign.</p>
              )}
            </TooltipContent>
          </Tooltip>

          {/* Sync Status */}
          {syncError ? (
            <Badge variant="destructive" className="gap-1">
              <AlertCircle className="w-3 h-3" />
              <span>Sync Error</span>
            </Badge>
          ) : isSynced ? (
            <Badge variant="outline" className="gap-1 text-green-600 border-green-500">
              <CheckCircle className="w-3 h-3" />
              <span>Synced</span>
            </Badge>
          ) : hasAssignedPhones ? (
            <Badge variant="outline" className="gap-1 text-yellow-600 border-yellow-500">
              Not synced
            </Badge>
          ) : null}
          </div>

          {/* Activate/Deactivate */}
          <div className="grid w-full grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] items-center gap-2 sm:flex sm:w-auto">
          {isActive ? (
            <Button
              variant="outline"
              size="sm"
              onClick={onToggleActive}
              disabled={isTogglingActive}
              className="h-10 px-2 text-orange-600 hover:text-orange-700 hover:bg-orange-50 sm:h-9 sm:px-3"
            >
              {isTogglingActive ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <PowerOff className="w-4 h-4 mr-2" />
              )}
              <span>Deactivate</span>
            </Button>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={onToggleActive}
              disabled={isTogglingActive}
              className="h-10 px-2 text-green-600 hover:text-green-700 hover:bg-green-50 sm:h-9 sm:px-3"
            >
              {isTogglingActive ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Power className="w-4 h-4 mr-2" />
              )}
              <span>Activate</span>
            </Button>
          )}

          <Button size="sm" onClick={onSave} disabled={isSaving} className="h-10 sm:h-9">
            {isSaving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            <span>Save</span>
          </Button>

          <div className="mx-1 hidden h-6 w-px bg-border sm:block" />

          <HelpDialog />
          </div>
        </div>
      </TooltipProvider>
    </div>
  )
}
